import React, { useState } from "react";
import { Key, Loader2 } from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { BRAND } from "@/lib/content";
import { Link } from "react-router-dom";

const AdminLogin = () => {
  const { login } = useAdminAuth();
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setLoading(true);
    setError(null);
    const ok = await login(key.trim());
    setLoading(false);
    if (!ok) setError("Clave inválida. Verifica tu ADMIN_API_KEY.");
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#0B0410] text-[#F9F6F0] font-sans-emb relative overflow-hidden flex items-center justify-center px-6"
    >
      {/* Ambient purple corner */}
      <div
        className="absolute -top-40 -left-40 w-[28rem] h-[28rem] rounded-full pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(circle, rgba(61,21,101,0.35) 0%, rgba(61,21,101,0) 70%)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-40 -right-40 w-[24rem] h-[24rem] rounded-full pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(circle, rgba(230,213,184,0.2) 0%, rgba(230,213,184,0) 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-12">
          <img
            src={BRAND.logoWhite}
            alt="EMB"
            className="h-11 w-11 object-contain"
          />
          <div className="leading-tight">
            <div className="font-display text-xl">EMB Consulting Group</div>
            <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8]">
              Panel administrativo
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-8 bg-[#E6D5B8]" />
          <span className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
            Acceso restringido
          </span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-light leading-[1.1] mb-4">
          Ingresa tu{" "}
          <span className="italic text-[#E6D5B8]">clave de acceso</span>.
        </h1>
        <p className="text-sm text-[#F9F6F0]/60 mb-10 font-light leading-relaxed max-w-sm">
          Esta zona es exclusiva para el equipo de gestión de EMB. Usa el
          ADMIN_API_KEY configurado en tu entorno backend.
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
              Clave
            </label>
            <div className="relative">
              <Key
                size={14}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A397B4]"
              />
              <input
                type={show ? "text" : "password"}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="ADMIN_API_KEY"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                data-testid="admin-login-input"
                className="w-full pl-10 pr-20 py-3.5 bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm text-sm text-[#F9F6F0] placeholder:text-[#A397B4]/50 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tracking-luxury uppercase text-[#A397B4] hover:text-[#E6D5B8]"
              >
                {show ? "Ocultar" : "Ver"}
              </button>
            </div>
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 px-3 py-2.5 rounded-sm"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !key.trim()}
            data-testid="admin-login-submit"
            className="w-full bg-[#E6D5B8] hover:bg-[#FBEFD3] disabled:opacity-60 disabled:cursor-not-allowed text-[#0B0410] py-3.5 text-xs tracking-luxury uppercase font-semibold rounded-sm transition-colors flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            <span>{loading ? "Verificando..." : "Entrar al panel"}</span>
          </button>
        </form>

        <div className="mt-12 pt-6 border-t border-white/10 flex items-center justify-between text-[10px] tracking-luxury uppercase text-[#A397B4]/70">
          <Link to="/" className="hover:text-[#E6D5B8]">
            ← Volver al sitio
          </Link>
          <span>v1 · sesión por pestaña</span>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
